import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { newClientUploadId } from "../api/evaluationVideoUploadApi";
import {
  runEvalUpload,
  startEvalUploadBacklogFlusher,
} from "../utils/evalUploadBacklog";
import { identifyTablePlayers } from "../api/playerIdentityApi";
import { captureVideoFrameJpeg } from "../utils/videoContainCoords";
import {
  IDENTITY_SAMPLE_MS,
  stampIdentityOnViewSetup,
} from "../utils/tablePlayerIdentity";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../utils/coachExerciseNavigation";
import {
  DEFAULT_WEB_EVAL_FPS,
  EVAL_RECORD_TIMESLICE_MS,
  EVAL_VIDEO_CAPTURE,
  MATCH_RECORD_CHUNK_MS,
  buildWebEvalCameraOptions,
  isWideArrayCameraLabel,
  createEvalMediaRecorder,
  evalVideoBitRate,
  enforceEvalCaptureFrameRate,
  ensureEvalStreamHasAudio,
  evalStreamHasLiveAudio,
  evalTrackMeetsMinCaptureFps,
  evalVideoExtension,
  getEvalUserMedia,
  getLastEvalMicError,
  mixEvalAudioIntoCaptureStream,
  normalizeWebEvalFacingMode,
  normalizeWebEvalFps,
  pickEvalVideoMimeType,
  readEvalTrackFrameRate,
  streamVideoDeviceId,
} from "../utils/evalVideoEncoding";
import { transcodeEvalClipToLowH264 } from "../utils/evalClipTranscode";
import {
  createLiveH264Session,
  isLiveH264Blob,
  liveH264EncodeWallMs,
} from "../utils/liveH264Capture";
import { createSidecarAudioRecorder } from "../utils/sidecarAudio";
import { replayDebug } from "../utils/tableReplayDebug";
import {
  beginEvalClipUpload,
  enqueueEvalClipJob,
  evalClipKeyClaimed,
  finishEvalClipUpload,
  releaseEvalClipClaim,
} from "../utils/evalClipUploadQueue";
import {
  DEFAULT_CAMERA_ZOOM,
  MAX_FALLBACK_CAMERA_ZOOM,
  applyTrackZoom,
  digitalCropSourceRect,
  identityTrackZoom,
  logicalZoomRange,
  normalizeCameraZoom,
  readTrackZoomRange,
  clampCameraZoom,
  wideZoomMin,
} from "../utils/cameraTrackZoom";
import {
  AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
  RECORDING_RESOLUTION_PRESETS,
  VIDEO_QUALITY_OPTIONS,
  getEffectiveClientCaptureSettings,
  isLiveTrainingDeviceEvalCaptureEnabled,
  isClientCameraRecordingEnabled,
  isInstantReplayEnabled,
  loadClientSettings,
  normalizeClientSettings,
  replayEncodeSettings,
  saveClientSettings,
} from "../utils/liveTrainingSettings";
import {
  CALIBRATION_FRAME_MAX_EDGE,
  jpegBase64FromDataUrl,
  mergeCoachCalibrationSettings,
  publicCameraOptions,
  resolutionLabelFromFrame,
} from "../utils/coachCameraCalibration";
import { isOffTableVideoRecording } from "../utils/offTableRecordingFlag";
import {
  buildCameraSetupId,
  normalizeCameraView,
} from "../utils/evaluationCameraSetup";
import {
  getEvalPreCaptureCountdownGate,
  resetEvalPreCaptureCountdownDedupe,
  subscribeEvalPreCaptureCountdownGate,
} from "../utils/evalPreCaptureCountdown";
import {
  kotMatchIdFromClipKey,
  resolveKotMatchRecordingTarget,
  shouldPrewarmKotMatchCamera,
} from "../utils/liveTrainingKotMatchRecording";
import { isKotExercise, isTeSequences } from "../utils/teExecutionType";
import {
  buildEvalClipUploadMeta,
  evalClipFilename,
} from "../utils/evalClipUploadMeta";
import { appendLiveTrainingEvalDebug } from "../utils/liveTrainingEvalDebugLog";
import {
  appendCaptureQos,
  effectiveBitrateBps,
  resolveCaptureQosRunId,
  setCaptureQosContext,
  startCaptureQosSampling,
  stopCaptureQosSampling,
} from "../utils/captureQos";
import { remainingSecFromWallClock } from "../utils/liveTrainingMediaSync";
import {
  getHeldEvalCameraStream,
  holdEvalCameraStream,
  parkHeldEvalCameraStream,
  releaseHeldEvalCameraStream,
  resumeHeldEvalCameraStream,
} from "../utils/evalCameraPermission";
import { COACH_TABLE_VIDEO_SOURCE } from "./useCoachTableVideoRecording";

const FLIP_KEY = "smarttt.liveTrainingEvalPreviewFlip";
const CAPTURE_KEY = "smarttt.liveTrainingEvalPreviewCapture";
/** Start camera this many seconds before the eval record window (not the whole session). */
const EVAL_CAMERA_PREWARM_SEC = 20;

function defaultEvalCaptureFps() {
  // Prefer client recordingFps (defaults to 60); never start below that.
  try {
    const fromClient = normalizeWebEvalFps(
      loadClientSettings()?.recordingFps,
      DEFAULT_WEB_EVAL_FPS
    );
    return fromClient || DEFAULT_WEB_EVAL_FPS;
  } catch {
    return DEFAULT_WEB_EVAL_FPS;
  }
}

function emptyCapturePrefs() {
  return {
    flipH: false,
    flipV: false,
    fps: defaultEvalCaptureFps(),
    facingMode: "environment",
    deviceId: "",
    zoom: DEFAULT_CAMERA_ZOOM,
    resolution: "",
  };
}

function readJson(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function loadCapturePrefs() {
  const fallback = emptyCapturePrefs();
  const stored = readJson(CAPTURE_KEY);
  const legacyFlip = readJson(FLIP_KEY);
  const src = stored && typeof stored === "object" ? stored : {};
  const flipSrc =
    stored && typeof stored === "object"
      ? stored
      : legacyFlip && typeof legacyFlip === "object"
        ? legacyFlip
        : {};
  return {
    flipH: flipSrc.flipH === true,
    flipV: flipSrc.flipV === true,
    fps: normalizeWebEvalFps(src.fps, defaultEvalCaptureFps()),
    facingMode: normalizeWebEvalFacingMode(src.facingMode, fallback.facingMode),
    deviceId: typeof src.deviceId === "string" ? src.deviceId : "",
    zoom: normalizeCameraZoom(src.zoom, fallback.zoom),
    resolution: RECORDING_RESOLUTION_PRESETS[
      String(src.resolution || "")
        .trim()
        .toLowerCase()
    ]
      ? String(src.resolution).trim().toLowerCase()
      : "",
  };
}

export function getStoredCameraFacingMode() {
  return loadCapturePrefs().facingMode;
}

function saveCapturePrefs(next) {
  const payload = {
    flipH: next?.flipH === true,
    flipV: next?.flipV === true,
    fps: normalizeWebEvalFps(next?.fps),
    facingMode: normalizeWebEvalFacingMode(next?.facingMode),
    deviceId: typeof next?.deviceId === "string" ? next.deviceId : "",
    zoom: normalizeCameraZoom(next?.zoom),
    resolution: RECORDING_RESOLUTION_PRESETS[
      String(next?.resolution || "")
        .trim()
        .toLowerCase()
    ]
      ? String(next.resolution).trim().toLowerCase()
      : "",
  };
  try {
    window.localStorage.setItem(CAPTURE_KEY, JSON.stringify(payload));
    window.localStorage.setItem(
      FLIP_KEY,
      JSON.stringify({ flipH: payload.flipH, flipV: payload.flipV })
    );
  } catch {
    /* ignore */
  }
  return payload;
}

function resolveSequence(exercise) {
  const seqs = [...(exercise?.sequences || [])].sort(
    (a, b) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0)
  );
  const cs = exercise?.currentSequence;
  let idx = 0;
  if (cs && typeof cs === "object") {
    const byTes = seqs.findIndex(
      (s) => cs.tes_id != null && String(s.tes_id) === String(cs.tes_id)
    );
    const byOrder = seqs.findIndex(
      (s) => cs.sequence_order != null && s.sequence_order === cs.sequence_order
    );
    idx = byTes >= 0 ? byTes : byOrder >= 0 ? byOrder : 0;
  } else if (typeof cs === "number") {
    const byOrder = seqs.findIndex((s) => s.sequence_order === cs);
    idx = byOrder >= 0 ? byOrder : Math.max(0, Math.min(seqs.length - 1, cs));
  }
  const obj = seqs[idx] || null;
  const number = obj?.sequence_order ?? (seqs.length ? idx + 1 : 1);
  return { number, obj };
}

function stopTracks(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach((t) => t.stop());
  } catch {
    /* ignore */
  }
}

function resolveUploadPlayerId(meta) {
  if (meta?.playerId) return meta.playerId;
  if (typeof window === "undefined") return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem("user") || "{}");
    return stored?.player_id || null;
  } catch {
    return null;
  }
}

/** Upload args shared by every staged KoT piece and the finalize call. */
function kotUploadArgs(meta, kotMatchId) {
  return {
    mimeType: "video/mp4",
    teId: meta.teId,
    playerId: resolveUploadPlayerId(meta),
    kotMatchId,
    tableNumber: meta.tableNumber,
    cameraPosition: meta.cameraPosition,
    viewSetup: {
      ...(meta.viewSetup || {}),
      client_codec: "h264",
      client_encoded: true,
      client_container: "mp4",
    },
    filename: evalClipFilename({
      tableNumber: meta.tableNumber,
      cameraPosition: meta.cameraPosition,
      cameraSetup: meta.cameraSetup,
      sequenceNumber: null,
      teId: meta.teId,
      ext: "mp4",
      kotMatchId,
    }),
  };
}

/** One KoT piece (picture + its mic slice); kept on the phone if it can't go up. */
function enqueueKotSegmentUpload({ clipKey, meta, kotMatchId, segmentIndex, blob, audioBlob }) {
  return new Promise((resolve) => {
    enqueueEvalClipJob(async () => {
      try {
        const result = await runEvalUpload("kot_segment", {
          args: { ...kotUploadArgs(meta, kotMatchId), segmentIndex },
          blob,
          audioBlob,
        });
        appendLiveTrainingEvalDebug(
          result?.saved_on_phone ? "kot_segment_saved_on_phone" : "kot_segment_uploaded",
          { clipKey, segmentIndex }
        );
        resolve(result);
      } catch (err) {
        appendLiveTrainingEvalDebug("kot_segment_upload_failed", {
          clipKey,
          segmentIndex,
          message: err?.message || String(err),
        });
        resolve(null);
      }
    });
  });
}

/**
 * Player-device evaluation capture for Live Training (web).
 * Records the last autoeval window at 720p 30/60fps H.264 low bitrate.
 */
/**
 * Camera size to request: the 720p eval capture, raised to the coach's replay
 * resolution when that is larger (iOS records the replay straight from the track).
 */
function cameraCaptureSize(tsId, resolutionOverride) {
  const override = String(resolutionOverride || "")
    .trim()
    .toLowerCase();
  const forced = RECORDING_RESOLUTION_PRESETS[override];
  if (forced) {
    return { captureWidth: forced.width, captureHeight: forced.height };
  }
  const replay = isInstantReplayEnabled(tsId) ? replayEncodeSettings(tsId) : null;
  const width = Math.max(EVAL_VIDEO_CAPTURE.width, replay?.width || 0);
  const height = Math.max(EVAL_VIDEO_CAPTURE.height, replay?.height || 0);
  return { captureWidth: width, captureHeight: height };
}

function waitForValidationJpeg(video, timeoutMs = 4000) {
  const started = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const jpeg = captureVideoFrameJpeg(
        video,
        0.62,
        CALIBRATION_FRAME_MAX_EDGE
      );
      if (jpeg) {
        resolve(jpeg);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, 100);
    };
    tick();
  });
}

export function useLiveTrainingEvalRecorder({
  enabled = false,
  /** When true, honor coach-phone opt-in instead of player capture switches. */
  coachDevice = false,
  tsId = null,
  exercise = null,
  exerciseStateId = null,
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  viewerPlayerId = null,
  viewerTableNumber = null,
  viewerSide = null,
  tablePhone = false,
  tablePlayers = null,
  /** Every table this phone was calibrated to film (main included). */
  tablePhoneTables = null,
  kotSnapshot = null,
  videoRef,
  canvasRef,
  /** Session socket — listens for coach_table_video start/stop. */
  socket = null,
}) {
  const [initialPrefs] = useState(loadCapturePrefs);
  const [recordingEnabled, setRecordingEnabled] = useState(() =>
    enabled
      ? isLiveTrainingDeviceEvalCaptureEnabled(tsId, {
          isCoachView: coachDevice,
        })
      : false
  );
  const [captureSettings, setCaptureSettings] = useState(() =>
    getEffectiveClientCaptureSettings(tsId)
  );
  const [cameraView, setCameraViewState] = useState(() =>
    normalizeCameraView(
      getEffectiveClientCaptureSettings(tsId).cameraView ||
        loadClientSettings().cameraView,
      "spectator"
    )
  );
  const cameraViewRef = useRef(cameraView);
  cameraViewRef.current = cameraView;
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [coachCalibHold, setCoachCalibHold] = useState(false);
  const calibrationOpenRef = useRef(false);
  calibrationOpenRef.current = calibrationOpen;
  const [flipH, setFlipH] = useState(initialPrefs.flipH);
  const [flipV, setFlipV] = useState(initialPrefs.flipV);
  const [fps, setFps] = useState(initialPrefs.fps);
  const [facingMode, setFacingMode] = useState(initialPrefs.facingMode);
  const [deviceId, setDeviceId] = useState(initialPrefs.deviceId);
  const [zoom, setZoom] = useState(initialPrefs.zoom);
  const [zoomRange, setZoomRange] = useState(null);
  const [hardwareZoom, setHardwareZoom] = useState(false);
  const [cameras, setCameras] = useState(() => buildWebEvalCameraOptions([]));
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  /** null while the mic is live; otherwise why clips would record without sound. */
  const [micError, setMicError] = useState(null);
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [recording, setRecording] = useState(false);
  /** WebCodecs path paints its own canvas; the hidden preview canvas can idle. */
  const [liveEncoding, setLiveEncoding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("idle");

  const streamRef = useRef(null);
  const tablePhoneRef = useRef(tablePhone);
  const tablePlayersRef = useRef(tablePlayers);
  const tablePhoneTablesRef = useRef(tablePhoneTables);
  tablePhoneRef.current = tablePhone;
  tablePlayersRef.current = tablePlayers;
  tablePhoneTablesRef.current = tablePhoneTables;
  const recordStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const liveCaptureRef = useRef(null);
  /**
   * KoT live capture: one-minute pieces uploaded during the match so only the
   * last minute is left to send when the match ends.
   * { timer, nextIndex, failed, pending: Promise|null }
   */
  const kotSegmentsRef = useRef(null);
  const sidecarAudioRef = useRef(null);
  const captureGenRef = useRef(0);
  const startLockRef = useRef(false);
  const chunksRef = useRef([]);
  const drawRafRef = useRef(0);
  const flipRef = useRef({ flipH, flipV });
  const fpsRef = useRef(fps);
  const resolutionOverrideRef = useRef(initialPrefs.resolution || "");
  const camerasRef = useRef([]);
  const coachCalibSeqRef = useRef(0);
  const calibChainRef = useRef(Promise.resolve());
  const calibHoldTimerRef = useRef(0);
  const facingRef = useRef(facingMode);
  const deviceIdRef = useRef(deviceId);
  const zoomRef = useRef(zoom);
  const zoomRangeRef = useRef(null);
  const hardwareZoomRef = useRef(false);
  const prefsRef = useRef(initialPrefs);
  const zoomApplyRafRef = useRef(0);
  const zoomUiRafRef = useRef(0);
  const camTokenRef = useRef(0);
  const cameraSwitchingRef = useRef(false);
  const clipKeyRef = useRef(null);
  const clipMetaRef = useRef(null);
  const identityStopRef = useRef({ exercise: false, eval: false });
  const identityPlayersRef = useRef(null);
  const identityInflightRef = useRef(false);
  const identityGenRef = useRef(0);
  const tsIdRef = useRef(tsId);
  tsIdRef.current = tsId;
  const recordingRef = useRef(false);
  const uploadInflightRef = useRef(0);
  const stopAndUploadRef = useRef(async () => {});
  /** Coach multi-table / table-cam capture — keep camera + recorder alive outside the autoeval window. */
  const coachRemoteActiveRef = useRef(false);
  const startCameraRef = useRef(async () => null);
  const startRecordingRef = useRef(() => {});
  const viewerTableNumberRef = useRef(viewerTableNumber);
  const exerciseRef = useRef(exercise);
  viewerTableNumberRef.current = viewerTableNumber;
  exerciseRef.current = exercise;

  flipRef.current = { flipH, flipV };
  fpsRef.current = fps;
  // deviceId / facing are owned by setCaptureCamera + applyTrackSettings.
  // Do not clobber them from React state mid-switch (stale renders broke
  // front↔back getUserMedia).
  if (!cameraSwitchingRef.current) {
    facingRef.current = facingMode;
    deviceIdRef.current = deviceId;
  }
  // Do not sync zoomRef from React state — setCaptureZoom owns live zoom so
  // pinch frames are not clobbered by a stale render mid-gesture.
  prefsRef.current = {
    flipH,
    flipV,
    fps,
    facingMode,
    deviceId,
    zoom: zoomRef.current,
    resolution: resolutionOverrideRef.current || "",
  };

  const isStarted = exerciseStateId === TE_STATE_STARTED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isKot = isKotExercise(exercise);
  const captureAllowed = Boolean(enabled && recordingEnabled);
  const kotTarget = isKot
    ? resolveKotMatchRecordingTarget({
        snapshot: kotSnapshot,
        viewerPlayerId,
        exerciseStateId,
        tableNumber: viewerTableNumber,
        tablePhone,
      })
    : null;
  const kotPrewarm =
    isKot &&
    shouldPrewarmKotMatchCamera({
      snapshot: kotSnapshot,
      viewerPlayerId,
      exerciseStateId,
      tableNumber: viewerTableNumber,
      tablePhone,
    });

  const seqTimerRaw = exercise?.sequenceTimer;
  const exTimerRaw = exercise?.timer;
  const seqHasTime = Number(seqTimerRaw) > 0;
  const isWaiting =
    !isStarted && !isPaused
      ? true
      : awaitingManualSequenceStart ||
        awaitingAutoSequenceStart ||
        (isPaused && !seqHasTime);

  const [now, setNow] = useState(() => Date.now());
  const anchorRef = useRef({ seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() });
  useEffect(() => {
    anchorRef.current = { seq: seqTimerRaw, ex: exTimerRaw, at: Date.now() };
    setNow(Date.now());
  }, [seqTimerRaw, exTimerRaw, exercise?.te_id, isPaused]);
  useEffect(() => {
    if (!captureAllowed || isWaiting || isPaused || isKot) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [captureAllowed, isWaiting, isPaused, isKot]);

  const elapsed = isPaused ? 0 : Math.floor((now - anchorRef.current.at) / 1000);
  const seqDisplay =
    seqTimerRaw != null ? Math.max(0, anchorRef.current.seq - elapsed) : null;
  const exDisplay =
    exTimerRaw != null ? Math.max(0, anchorRef.current.ex - elapsed) : null;

  const { number: seqNumber, obj: currentSeq } = resolveSequence(exercise);
  const sequenceDurationSec = currentSeq
    ? (parseInt(currentSeq.sequence_duration, 10) || 0) * 60
    : 0;
  const wallSeqRemaining = remainingSecFromWallClock(
    exercise?.sequenceStartTime ?? exercise?.media_sync_at,
    sequenceDurationSec,
    now
  );
  const remainingSec = isTeSequences(exercise)
    ? wallSeqRemaining != null && isStarted && !isPaused && !isWaiting
      ? wallSeqRemaining
      : seqDisplay
    : exDisplay;
  const windowSec = Number(captureSettings.autoevalVideoMaxDurationSec) || 60;
  const evalPlayKey = `${exercise?.te_id || ""}:${seqNumber ?? ""}`;
  const [countdownGate, setCountdownGate] = useState(getEvalPreCaptureCountdownGate);

  useEffect(() => subscribeEvalPreCaptureCountdownGate(setCountdownGate), []);

  const countdownBlocksRecording =
    isTeSequences(exercise) &&
    sequenceDurationSec > windowSec &&
    countdownGate.blocking &&
    (!countdownGate.playKey || countdownGate.playKey === evalPlayKey) &&
    remainingSec != null &&
    Number(remainingSec) > windowSec;
  // Prefer session / device capture quality (coach can force via constraints).
  const videoQuality = captureSettings.videoQuality || "low";
  const captureLocked = Boolean(captureSettings.forceClientCaptureSettings);
  const recordingResolution = captureSettings.recordingResolution || "720p";

  useEffect(() => {
    const sync = () => {
      setRecordingEnabled(
        enabled
          ? isLiveTrainingDeviceEvalCaptureEnabled(tsId, {
              isCoachView: coachDevice,
            })
          : false
      );
      const next = getEffectiveClientCaptureSettings(tsId);
      setCaptureSettings(next);
      setCameraViewState(
        normalizeCameraView(
          next.cameraView || loadClientSettings().cameraView,
          "spectator"
        )
      );
    };
    sync();
    window.addEventListener("smarttt:live-training-forced-constraints", sync);
    window.addEventListener("smarttt:live-training-coach-settings", sync);
    window.addEventListener("smarttt:live-training-client-settings", sync);
    return () => {
      window.removeEventListener(
        "smarttt:live-training-forced-constraints",
        sync
      );
      window.removeEventListener("smarttt:live-training-coach-settings", sync);
      window.removeEventListener("smarttt:live-training-client-settings", sync);
    };
  }, [enabled, coachDevice, tsId]);

  const setCameraView = useCallback((nextView) => {
    const view = normalizeCameraView(nextView, "spectator") || "spectator";
    setCameraViewState(view);
    cameraViewRef.current = view;
    saveClientSettings(
      normalizeClientSettings({
        ...loadClientSettings(),
        cameraView: view,
      })
    );
  }, []);

  useEffect(() => {
    if (!enabled || !recordingEnabled) {
      setCalibrationOpen(false);
    }
  }, [enabled, recordingEnabled]);

  const persistPrefs = useCallback((partial) => {
    const next = saveCapturePrefs({ ...prefsRef.current, ...partial });
    prefsRef.current = next;
    return next;
  }, []);

  const toggleFlipV = useCallback(() => {
    setFlipV((prev) => {
      persistPrefs({ flipH: flipRef.current.flipH, flipV: !prev });
      return !prev;
    });
  }, [persistPrefs]);

  const toggleFlipH = useCallback(() => {
    setFlipH((prev) => {
      persistPrefs({ flipH: !prev, flipV: flipRef.current.flipV });
      return !prev;
    });
  }, [persistPrefs]);

  const drawFrame = useCallback(() => {
    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;
    const w = canvas.width || EVAL_VIDEO_CAPTURE.width;
    const h = canvas.height || EVAL_VIDEO_CAPTURE.height;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    // Keep the last frame on screen while the lens stream is reconnecting.
    if (cameraSwitchingRef.current && video.readyState < 2) {
      return;
    }
    const { flipH: fh, flipV: fv } = flipRef.current;
    ctx.save();
    ctx.setTransform(fh ? -1 : 1, 0, 0, fv ? -1 : 1, fh ? w : 0, fv ? h : 0);
    if (video.readyState >= 2) {
      const digitalZoom =
        !hardwareZoomRef.current && zoomRef.current > 1.001
          ? zoomRef.current
          : 1;
      if (digitalZoom > 1.001) {
        const crop = digitalCropSourceRect(
          video.videoWidth || w,
          video.videoHeight || h,
          digitalZoom
        );
        ctx.drawImage(
          video,
          crop.sx,
          crop.sy,
          crop.sw,
          crop.sh,
          0,
          0,
          w,
          h
        );
      } else {
        ctx.drawImage(video, 0, 0, w, h);
      }
    } else {
      ctx.fillStyle = "#0b1220";
      ctx.fillRect(0, 0, w, h);
    }
    ctx.restore();
  }, [canvasRef, videoRef]);

  const applyZoomToStream = useCallback(
    async (stream, nextZoom = zoomRef.current) => {
      const track = stream?.getVideoTracks?.()?.[0];
      const range = readTrackZoomRange(track);
      if (range) {
        zoomRangeRef.current = range;
        setZoomRange((prev) => {
          if (
            prev &&
            Number(prev.min) === Number(range.min) &&
            Number(prev.max) === Number(range.max)
          ) {
            return prev;
          }
          return range;
        });
      } else if (zoomRangeRef.current) {
        zoomRangeRef.current = null;
        setZoomRange(null);
      }
      const desired = normalizeCameraZoom(nextZoom);
      if (!track) {
        if (hardwareZoomRef.current) {
          hardwareZoomRef.current = false;
          setHardwareZoom(false);
        }
        return { hardware: false, zoom: desired, range: null };
      }
      const result = await applyTrackZoom(track, desired);
      if (result.hardware !== hardwareZoomRef.current) {
        hardwareZoomRef.current = result.hardware;
        setHardwareZoom(result.hardware);
      }
      if (result.range) {
        zoomRangeRef.current = result.range;
        setZoomRange((prev) => {
          if (
            prev &&
            Number(prev.min) === Number(result.range.min) &&
            Number(prev.max) === Number(result.range.max)
          ) {
            return prev;
          }
          return result.range;
        });
      }
      return result;
    },
    []
  );

  /** Native 1× on the track; calibration then zooms the preview digitally. */
  const resetTrackToIdentityZoom = useCallback(async (stream) => {
    const track = stream?.getVideoTracks?.()?.[0];
    const range = readTrackZoomRange(track);
    if (track && range) {
      const identity = identityTrackZoom(range);
      try {
        await track.applyConstraints({ advanced: [{ zoom: identity }] });
      } catch {
        try {
          await track.applyConstraints({ zoom: identity });
        } catch {
          /* ignore */
        }
      }
    }
    if (hardwareZoomRef.current) {
      hardwareZoomRef.current = false;
      setHardwareZoom(false);
    }
  }, []);

  useEffect(() => {
    // Paint every frame while recording so canvas.captureStream stays live.
    // Live H.264 paints its own encoder canvas — a second 720p60 paint here
    // only burns GPU/thermal headroom and costs encoder frames on phones.
    const needDraw = calibrationOpen || (recording && !liveEncoding);
    if (!needDraw) return undefined;
    let alive = true;
    const loop = () => {
      if (!alive) return;
      drawFrame();
      drawRafRef.current = window.requestAnimationFrame(loop);
    };
    drawRafRef.current = window.requestAnimationFrame(loop);
    return () => {
      alive = false;
      window.cancelAnimationFrame(drawRafRef.current);
    };
  }, [calibrationOpen, drawFrame, recording, liveEncoding]);

  const attachCameraToVideo = useCallback(
    async (stream) => {
      const video = videoRef?.current;
      if (!video || !stream) return;
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");
      try {
        await video.play();
      } catch {
        /* autoplay can wait for user gesture (calibration open) */
      }
    },
    [videoRef]
  );

  const refreshCameras = useCallback(async () => {
    if (!navigator?.mediaDevices?.enumerateDevices) {
      const empty = buildWebEvalCameraOptions([]);
      setCameras(empty);
      camerasRef.current = empty;
      return empty;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const listed = buildWebEvalCameraOptions(
        devices.filter((device) => device.kind === "videoinput"),
        { keepDeviceId: deviceIdRef.current }
      );
      setCameras(listed);
      camerasRef.current = listed;
      return listed;
    } catch {
      const empty = buildWebEvalCameraOptions([]);
      setCameras(empty);
      camerasRef.current = empty;
      return empty;
    }
  }, []);

  const applyTrackSettings = useCallback(
    (stream) => {
      const track = stream?.getVideoTracks?.()?.[0];
      const settings = track?.getSettings?.() || {};
      const nextDeviceId =
        typeof settings.deviceId === "string" ? settings.deviceId : "";
      const nextFacing = normalizeWebEvalFacingMode(
        settings.facingMode,
        facingRef.current
      );
      if (nextDeviceId && nextDeviceId !== deviceIdRef.current) {
        deviceIdRef.current = nextDeviceId;
        setDeviceId(nextDeviceId);
      }
      if (nextFacing !== facingRef.current) {
        facingRef.current = nextFacing;
        setFacingMode(nextFacing);
      }
      persistPrefs({
        deviceId: deviceIdRef.current,
        facingMode: facingRef.current,
        fps: fpsRef.current,
        zoom: zoomRef.current,
      });
    },
    [persistPrefs]
  );

  const syncMicState = useCallback((stream) => {
    const live = evalStreamHasLiveAudio(stream);
    const reason = live ? null : getLastEvalMicError() || "missing";
    setMicError(reason);
    return { has_audio: live, mic_error: reason };
  }, []);

  const startCamera = useCallback(
    async ({ forceRestart = false } = {}) => {
      if (streamRef.current && !forceRestart) {
        resumeHeldEvalCameraStream();
        await attachCameraToVideo(streamRef.current);
        setCameraReady(true);
        await refreshCameras();
        return streamRef.current;
      }
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCameraError("unsupported");
        setStatus("error");
        return null;
      }
      const token = ++camTokenRef.current;

      // Prefer the stream acquired on the join / Allow tap — avoids iOS re-prompt.
      // Re-open when the held track is stuck at a low rate (common on front cams).
      const held = !forceRestart ? getHeldEvalCameraStream() : null;
      if (held) {
        // The join / Allow tap opens the default back lens; reopen the lens the
        // player picked instead of adopting (and saving) that one.
        const heldDeviceId = streamVideoDeviceId(held);
        if (
          deviceIdRef.current &&
          heldDeviceId &&
          heldDeviceId !== deviceIdRef.current
        ) {
          appendLiveTrainingEvalDebug("camera_held_wrong_lens", {
            held: heldDeviceId,
            want: deviceIdRef.current,
          });
          return startCamera({ forceRestart: true });
        }
        resumeHeldEvalCameraStream();
        await ensureEvalStreamHasAudio(held);
        await enforceEvalCaptureFrameRate(held, fpsRef.current);
        const heldRate = readEvalTrackFrameRate(held);
        if (!evalTrackMeetsMinCaptureFps(held)) {
          appendLiveTrainingEvalDebug("camera_held_low_fps", {
            heldRate,
            wantRate: normalizeWebEvalFps(
              fpsRef.current,
              DEFAULT_WEB_EVAL_FPS
            ),
            facing: facingRef.current,
          });
          return startCamera({ forceRestart: true });
        }
        if (streamRef.current && streamRef.current !== held) {
          // Detach previous local ref without stopping the held session stream.
          streamRef.current = null;
        }
        streamRef.current = held;
        applyTrackSettings(held);
        await applyZoomToStream(held, zoomRef.current);
        const canvasHeld = canvasRef?.current;
        if (canvasHeld) {
          canvasHeld.width = EVAL_VIDEO_CAPTURE.width;
          canvasHeld.height = EVAL_VIDEO_CAPTURE.height;
        }
        await attachCameraToVideo(held);
        await refreshCameras();
        setCameraError(null);
        setCameraReady(true);
        setStatus((prev) => (prev === "error" ? "idle" : prev));
        appendLiveTrainingEvalDebug("camera_ready", {
          facing: facingRef.current,
          fps: fpsRef.current,
          trackFps: heldRate,
          reusedHeld: true,
          ...syncMicState(held),
        });
        return held;
      }

      if (forceRestart) {
        const prev = streamRef.current || getHeldEvalCameraStream();
        streamRef.current = null;
        setCameraReady(false);
        // Leave video.srcObject until the new stream attaches so the last
        // frame freezes instead of flashing black.
        try {
          prev?.getTracks?.().forEach((t) => t.stop());
        } catch {
          /* ignore */
        }
        if (getHeldEvalCameraStream() === prev || !getHeldEvalCameraStream()) {
          // Ensure hold is cleared so the new device becomes the session stream.
          try {
            const leftover = getHeldEvalCameraStream();
            leftover?.getTracks?.().forEach((t) => t.stop());
          } catch {
            /* ignore */
          }
        }
        // Clear module hold without relying on release if already stopped.
        releaseHeldEvalCameraStream();
        // Hardware needs a beat after stop before another lens can open (esp. iOS).
        await new Promise((resolve) => window.setTimeout(resolve, 120));
        if (token !== camTokenRef.current) return null;
      }
      try {
        let stream;
        const size = cameraCaptureSize(
          tsIdRef.current,
          resolutionOverrideRef.current
        );
        try {
          stream = await getEvalUserMedia({
            fps: fpsRef.current,
            deviceId: deviceIdRef.current,
            facingMode: facingRef.current,
            ...size,
          });
        } catch (err) {
          if (!deviceIdRef.current) throw err;
          try {
            stream = await getEvalUserMedia({
              fps: fpsRef.current,
              deviceId: deviceIdRef.current,
              facingMode: facingRef.current,
              deviceIdMode: "ideal",
              ...size,
            });
          } catch {
            stream = await getEvalUserMedia({
              fps: fpsRef.current,
              facingMode: facingRef.current,
              ...size,
            });
          }
        }
        if (token !== camTokenRef.current) {
          stopTracks(stream);
          return null;
        }
        await enforceEvalCaptureFrameRate(stream, fpsRef.current);
        await ensureEvalStreamHasAudio(stream, { timeoutMs: 4000 });
        holdEvalCameraStream(stream);
        streamRef.current = stream;
        applyTrackSettings(stream);
        await applyZoomToStream(stream, zoomRef.current);
        const canvas = canvasRef?.current;
        if (canvas) {
          canvas.width = EVAL_VIDEO_CAPTURE.width;
          canvas.height = EVAL_VIDEO_CAPTURE.height;
        }
        await attachCameraToVideo(stream);
        await refreshCameras();
        setCameraError(null);
        setCameraReady(true);
        setStatus((prev) => (prev === "error" ? "idle" : prev));
        appendLiveTrainingEvalDebug("camera_ready", {
          facing: facingRef.current,
          fps: fpsRef.current,
          trackFps: readEvalTrackFrameRate(stream),
          reusedHeld: false,
          ...syncMicState(stream),
        });
        return stream;
      } catch (err) {
        if (token !== camTokenRef.current) return null;
        console.warn("Live training eval camera failed:", err?.message || err);
        appendLiveTrainingEvalDebug("camera_failed", err?.message || String(err));
        setCameraError("denied");
        setCameraReady(false);
        setStatus("error");
        return null;
      }
    },
    [
      applyTrackSettings,
      applyZoomToStream,
      attachCameraToVideo,
      canvasRef,
      refreshCameras,
      syncMicState,
    ]
  );

  const stopCamera = useCallback(() => {
    camTokenRef.current += 1;
    // Park the session-held stream (keeps iOS permission) instead of stopping.
    const held = getHeldEvalCameraStream();
    if (held && streamRef.current === held) {
      parkHeldEvalCameraStream();
      streamRef.current = held;
    } else {
      stopTracks(streamRef.current);
      streamRef.current = null;
    }
    stopTracks(recordStreamRef.current);
    recordStreamRef.current = null;
    const video = videoRef?.current;
    if (video) video.srcObject = null;
    setCameraReady(false);
  }, [videoRef]);

  const releaseCameraSession = useCallback(() => {
    camTokenRef.current += 1;
    stopTracks(recordStreamRef.current);
    recordStreamRef.current = null;
    streamRef.current = null;
    const video = videoRef?.current;
    if (video) video.srcObject = null;
    releaseHeldEvalCameraStream();
    setCameraReady(false);
  }, [videoRef]);

  const setCaptureFps = useCallback(
    (nextFps) => {
      if (getEffectiveClientCaptureSettings(tsId).forceClientCaptureSettings) {
        return;
      }
      const next = normalizeWebEvalFps(nextFps);
      if (next === fpsRef.current) return;
      fpsRef.current = next;
      setFps(next);
      persistPrefs({ fps: next });
      saveClientSettings({
        ...loadClientSettings(),
        recordingFps: next,
      });
      setCaptureSettings(getEffectiveClientCaptureSettings(tsId));
      if (!recordingRef.current) {
        startCamera({ forceRestart: true });
      }
    },
    [persistPrefs, startCamera, tsId]
  );

  const setCaptureVideoQuality = useCallback(
    (nextQuality) => {
      if (getEffectiveClientCaptureSettings(tsId).forceClientCaptureSettings) {
        return;
      }
      const q = String(nextQuality || "")
        .trim()
        .toLowerCase();
      if (!VIDEO_QUALITY_OPTIONS.includes(q)) return;
      saveClientSettings({
        ...loadClientSettings(),
        videoQuality: q,
      });
      setCaptureSettings(getEffectiveClientCaptureSettings(tsId));
    },
    [tsId]
  );

  const setCaptureDurationSec = useCallback(
    (nextSec) => {
      // Coach session duration is always applied via forced constraints when
      // published; local edits only when not locked by force-client-capture.
      const effective = getEffectiveClientCaptureSettings(tsId);
      if (effective.forceClientCaptureSettings) return;
      const n = Number(nextSec);
      if (!AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS.includes(n)) return;
      saveClientSettings({
        ...loadClientSettings(),
        autoevalVideoMaxDurationSec: n,
      });
      setCaptureSettings(getEffectiveClientCaptureSettings(tsId));
    },
    [tsId]
  );

  const openCameraStreamForSelection = useCallback(
    async ({ deviceId: wantDeviceId, facingMode: wantFacing, fps: wantFps }) => {
      const tryOpen = (opts) =>
        getEvalUserMedia({
          fps: wantFps,
          ...cameraCaptureSize(tsIdRef.current, resolutionOverrideRef.current),
          ...opts,
        });
      try {
        return await tryOpen({
          deviceId: wantDeviceId,
          facingMode: wantFacing,
        });
      } catch (err) {
        if (!wantDeviceId) throw err;
        try {
          return await tryOpen({
            deviceId: wantDeviceId,
            facingMode: wantFacing,
            deviceIdMode: "ideal",
          });
        } catch {
          return tryOpen({ facingMode: wantFacing });
        }
      }
    },
    []
  );

  const setCaptureCamera = useCallback(
    async (option) => {
      if (!option || recordingRef.current) return;
      const nextDeviceId = String(option?.deviceId || "");
      const nextFacing = normalizeWebEvalFacingMode(
        option?.facingMode ||
          (option?.lens === "front" ? "user" : "environment")
      );
      const alreadyOnDevice =
        nextDeviceId && nextDeviceId === deviceIdRef.current;
      const alreadyOnFacing =
        !nextDeviceId && nextFacing === facingRef.current;
      if ((alreadyOnDevice || alreadyOnFacing) && streamRef.current) {
        return;
      }

      const previousDeviceId = deviceIdRef.current;
      const previousFacing = facingRef.current;
      const previousZoom = zoomRef.current;
      // Dual Wide used as the Wide chip must open on the ultra end (0.5×).
      // A real Back Ultra Wide device stays at native 1×.
      const preferWideZoom =
        option?.lens === "ultrawide" &&
        (option.zoom > 0 && option.zoom < 1
          ? normalizeCameraZoom(option.zoom)
          : isWideArrayCameraLabel(option.label)
            ? 0.5
            : null);
      const nextZoom = preferWideZoom || DEFAULT_CAMERA_ZOOM;
      const token = ++camTokenRef.current;
      cameraSwitchingRef.current = true;
      setCameraSwitching(true);

      deviceIdRef.current = nextDeviceId;
      facingRef.current = nextFacing;
      setDeviceId(nextDeviceId);
      setFacingMode(nextFacing);
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      persistPrefs({
        deviceId: nextDeviceId,
        facingMode: nextFacing,
        zoom: nextZoom,
      });

      const stopPrevious = () => {
        const prev = streamRef.current || getHeldEvalCameraStream();
        streamRef.current = null;
        // Leave video.srcObject until the new stream attaches so the last
        // frame freezes instead of flashing black.
        try {
          prev?.getTracks?.().forEach((t) => t.stop());
        } catch {
          /* ignore */
        }
        releaseHeldEvalCameraStream();
      };

      const attachOpened = async (stream, { facing } = {}) => {
        await enforceEvalCaptureFrameRate(stream, fpsRef.current);
        await ensureEvalStreamHasAudio(stream, { timeoutMs: 4000 });
        holdEvalCameraStream(stream);
        streamRef.current = stream;
        applyTrackSettings(stream);
        // Keep the requested facing even when the track omits facingMode.
        if (facing) {
          facingRef.current = facing;
          setFacingMode(facing);
        }
        await applyZoomToStream(stream, zoomRef.current);
        const canvas = canvasRef?.current;
        if (canvas) {
          canvas.width = EVAL_VIDEO_CAPTURE.width;
          canvas.height = EVAL_VIDEO_CAPTURE.height;
        }
        await attachCameraToVideo(stream);
        await refreshCameras();
        setCameraError(null);
        setCameraReady(true);
        setStatus((prev) => (prev === "error" ? "idle" : prev));
        appendLiveTrainingEvalDebug("camera_switched", {
          facing: facingRef.current,
          deviceId: deviceIdRef.current,
          lens: option?.lens || null,
          ...syncMicState(stream),
        });
      };

      try {
        stopPrevious();
        await new Promise((resolve) => window.setTimeout(resolve, 120));
        if (token !== camTokenRef.current) return;

        const stream = await openCameraStreamForSelection({
          deviceId: nextDeviceId,
          facingMode: nextFacing,
          fps: fpsRef.current,
        });
        if (token !== camTokenRef.current) {
          stopTracks(stream);
          return;
        }
        await attachOpened(stream, { facing: nextFacing });
      } catch (err) {
        if (token !== camTokenRef.current) return;
        console.warn(
          "Live training eval camera switch failed:",
          err?.message || err
        );
        appendLiveTrainingEvalDebug(
          "camera_switch_failed",
          err?.message || String(err)
        );
        deviceIdRef.current = previousDeviceId;
        facingRef.current = previousFacing;
        setDeviceId(previousDeviceId);
        setFacingMode(previousFacing);
        zoomRef.current = previousZoom;
        setZoom(previousZoom);
        persistPrefs({
          deviceId: previousDeviceId,
          facingMode: previousFacing,
          zoom: previousZoom,
        });
        try {
          const restored = await openCameraStreamForSelection({
            deviceId: previousDeviceId,
            facingMode: previousFacing,
            fps: fpsRef.current,
          });
          if (token !== camTokenRef.current) {
            stopTracks(restored);
            return;
          }
          await attachOpened(restored, { facing: previousFacing });
        } catch (restoreErr) {
          console.warn(
            "Live training eval camera restore failed:",
            restoreErr?.message || restoreErr
          );
          setCameraError("denied");
          setCameraReady(false);
          setStatus("error");
        }
      } finally {
        if (token === camTokenRef.current) {
          cameraSwitchingRef.current = false;
          setCameraSwitching(false);
        }
      }
    },
    [
      applyTrackSettings,
      applyZoomToStream,
      attachCameraToVideo,
      canvasRef,
      openCameraStreamForSelection,
      persistPrefs,
      refreshCameras,
      syncMicState,
      videoRef,
    ]
  );

  const armCoachCalibHold = useCallback(() => {
    setCoachCalibHold(true);
    window.clearTimeout(calibHoldTimerRef.current);
    calibHoldTimerRef.current = window.setTimeout(() => {
      setCoachCalibHold(false);
    }, 90000);
  }, []);

  const releaseCoachCalibHold = useCallback(() => {
    window.clearTimeout(calibHoldTimerRef.current);
    calibHoldTimerRef.current = 0;
    setCoachCalibHold(false);
  }, []);

  /**
   * Coach remote calibration. Opens the player camera, applies FPS / resolution
   * / lens, and returns one JPEG. Unchanged settings do not recapture.
   */
  const applyCoachCameraCalibration = useCallback(
    async (request = {}) => {
      const reason = request?.reason === "settings" ? "settings" : "open";
      const recordingNow =
        recordingRef.current || isOffTableVideoRecording();
      if (reason === "settings" && recordingNow) {
        return { ok: false, code: "recording" };
      }
      const current = {
        fps: fpsRef.current,
        resolution: resolutionOverrideRef.current || "",
        deviceId: deviceIdRef.current,
        facingMode: facingRef.current,
        lens: "",
      };
      const next =
        reason === "settings"
          ? mergeCoachCalibrationSettings(current, request.settings)
          : current;
      const fpsChanged = next.fps != null && next.fps !== current.fps;
      const resChanged = next.resolution !== current.resolution;
      const lensChanged =
        next.deviceId !== current.deviceId ||
        (!next.deviceId &&
          Boolean(next.facingMode) &&
          next.facingMode !== current.facingMode);
      if (reason === "settings" && !fpsChanged && !resChanged && !lensChanged) {
        return { ok: true, unchanged: true };
      }

      if (fpsChanged) {
        fpsRef.current = next.fps;
        setFps(next.fps);
        saveClientSettings({
          ...loadClientSettings(),
          recordingFps: next.fps,
        });
      }
      if (resChanged) {
        resolutionOverrideRef.current = next.resolution;
      }
      if (fpsChanged || resChanged) {
        persistPrefs({
          fps: fpsRef.current,
          resolution: resolutionOverrideRef.current || "",
        });
      }

      let restarted = false;
      if (lensChanged) {
        const before = camTokenRef.current;
        await setCaptureCamera({
          deviceId: next.deviceId,
          facingMode: next.facingMode || "environment",
          lens: next.lens,
        });
        restarted = camTokenRef.current !== before;
      }
      if ((fpsChanged || resChanged) && !restarted) {
        await startCamera({ forceRestart: true });
        restarted = true;
      }
      if (!restarted) {
        const stream = await startCamera();
        if (!stream && !streamRef.current) {
          return { ok: false, code: "camera_unavailable" };
        }
      }

      const video = videoRef?.current;
      const jpeg = await waitForValidationJpeg(video);
      const frame = jpeg ? jpegBase64FromDataUrl(jpeg) : null;
      if (!frame) return { ok: false, code: "camera_unavailable" };

      const track = streamRef.current?.getVideoTracks?.()?.[0];
      const picture = track?.getSettings?.() || {};
      const width = Number(picture.width) || video?.videoWidth || 0;
      const height = Number(picture.height) || video?.videoHeight || 0;
      const trackFps = Number(picture.frameRate);
      const listed = (await refreshCameras()) || camerasRef.current || [];
      const matched = listed.find(
        (camera) => camera.deviceId && camera.deviceId === deviceIdRef.current
      );
      return {
        ok: true,
        frame_jpeg: frame,
        width,
        height,
        fps:
          Number.isFinite(trackFps) && trackFps > 0
            ? Math.round(trackFps)
            : fpsRef.current,
        applied: {
          fps: fpsRef.current,
          resolution:
            resolutionOverrideRef.current ||
            resolutionLabelFromFrame(width, height) ||
            "",
          device_id: deviceIdRef.current,
          facing_mode: facingRef.current,
          lens: matched?.lens || next.lens || "",
        },
        cameras: publicCameraOptions(listed),
      };
    },
    [persistPrefs, refreshCameras, setCaptureCamera, startCamera, videoRef]
  );
  const applyCoachCameraCalibrationRef = useRef(applyCoachCameraCalibration);
  applyCoachCameraCalibrationRef.current = applyCoachCameraCalibration;

  const setCaptureZoom = useCallback(
    (nextZoom, { persist = true, applyHardware = true } = {}) => {
      const logical = logicalZoomRange(zoomRangeRef.current);
      const next = clampCameraZoom(nextZoom, logical);
      const leavingWide = zoomRef.current < 1 && next >= 1;
      zoomRef.current = next;
      // The wide lens is optical; drop back to native 1× so the digital
      // calibration preview zooms the normal lens.
      if (leavingWide && !applyHardware && streamRef.current) {
        void resetTrackToIdentityZoom(streamRef.current);
      }
      prefsRef.current = { ...prefsRef.current, zoom: next };
      if (persist) {
        persistPrefs({ zoom: next });
        // Flush React zoom immediately on gesture commit so the preview
        // style does not snap back to a one-frame-stale value.
        if (zoomUiRafRef.current) {
          window.cancelAnimationFrame(zoomUiRafRef.current);
          zoomUiRafRef.current = 0;
        }
        setZoom(next);
      } else if (!zoomUiRafRef.current) {
        // Coalesce React zoom state to one update per frame (fluid pinch).
        zoomUiRafRef.current = window.requestAnimationFrame(() => {
          zoomUiRafRef.current = 0;
          setZoom(zoomRef.current);
        });
      }
      if (!applyHardware || !streamRef.current) return next;
      if (zoomApplyRafRef.current) return next;
      zoomApplyRafRef.current = window.requestAnimationFrame(() => {
        zoomApplyRafRef.current = 0;
        if (streamRef.current) {
          applyZoomToStream(streamRef.current, zoomRef.current);
        }
      });
      return next;
    },
    [applyZoomToStream, persistPrefs, resetTrackToIdentityZoom]
  );

  /**
   * Wide lens reached by zooming the open camera out (e.g. Samsung 0.6×), or
   * back to its normal 1×. Applied on the camera even while calibrating:
   * a digital preview cannot show a wider view than the lens gives.
   */
  const setCaptureWideZoom = useCallback(
    async (wide) => {
      if (recordingRef.current) return zoomRef.current;
      const min = wideZoomMin(zoomRangeRef.current);
      const next = wide && min != null ? min : DEFAULT_CAMERA_ZOOM;
      zoomRef.current = next;
      prefsRef.current = { ...prefsRef.current, zoom: next };
      persistPrefs({ zoom: next });
      setZoom(next);
      const stream = streamRef.current;
      if (!stream) return next;
      if (next < 1 || !calibrationOpenRef.current) {
        await applyZoomToStream(stream, next);
      } else {
        await resetTrackToIdentityZoom(stream);
      }
      appendLiveTrainingEvalDebug("camera_wide_zoom", { zoom: next, min });
      return next;
    },
    [applyZoomToStream, persistPrefs, resetTrackToIdentityZoom]
  );

  const buildRecordStream = useCallback(() => {
    // Always encode from a 1280×720 canvas so resolution/flips are fixed and
    // MediaRecorder bitrate targets apply to Low (~900 kbps), not a 1080p+
    // camera track. Digital zoom crops here; hardware zoom is already on the
    // live track that we paint from.
    const canvas = canvasRef?.current;
    if (canvas && typeof canvas.captureStream === "function") {
      try {
        canvas.width = EVAL_VIDEO_CAPTURE.width;
        canvas.height = EVAL_VIDEO_CAPTURE.height;
        drawFrame();
        const captured = canvas.captureStream(
          fpsRef.current || DEFAULT_WEB_EVAL_FPS
        );
        return mixEvalAudioIntoCaptureStream(captured, streamRef.current);
      } catch {
        /* fall through to the live camera track */
      }
    }
    return streamRef.current;
  }, [canvasRef, drawFrame]);

  const armClipMeta = useCallback(
    (clipKey) => {
      const { number, obj } = resolveSequence(exercise);
      const kotMatchId = kotMatchIdFromClipKey(clipKey);
      const resolvedSetup = buildCameraSetupId(
        cameraViewRef.current,
        viewerSide || "A"
      );
      clipMetaRef.current = buildEvalClipUploadMeta({
        teId: exercise?.te_id,
        sequenceNumber: kotMatchId ? null : number,
        sequence: obj,
        viewerPlayerId,
        tableNumber: viewerTableNumber,
        cameraPosition: viewerSide,
        cameraSetup: resolvedSetup,
        recordedAt: new Date(),
        flipH: flipRef.current.flipH,
        flipV: flipRef.current.flipV,
        kotMatchId,
        tablePhone: tablePhoneRef.current,
        tablePlayers: tablePlayersRef.current,
        tableNumbers: tablePhoneTablesRef.current,
      });
      if (clipMetaRef.current) {
        clipMetaRef.current.clientUploadId = newClientUploadId();
      }
      clipKeyRef.current = clipKey;
      recordingRef.current = true;
      setRecording(true);
      setStatus("recording");
      appendLiveTrainingEvalDebug("recording_started", {
        clipKey,
        teId: exercise?.te_id,
        seq: number,
        liveH264: Boolean(liveCaptureRef.current),
        has_audio: evalStreamHasLiveAudio(streamRef.current),
        mic_error: evalStreamHasLiveAudio(streamRef.current)
          ? null
          : getLastEvalMicError() || "missing",
      });
      setCaptureQosContext({
        flow: "eval",
        session_id: String(exercise?.te_id || ""),
        ts_id: exercise?.ts_id ? String(exercise.ts_id) : null,
        clip_id: clipKey,
        run_id: resolveCaptureQosRunId(),
      });
      startCaptureQosSampling(() => ({
        clip_id: clipKeyRef.current,
        queue_depth: uploadInflightRef.current,
      }));
    },
    [exercise, viewerPlayerId, viewerSide, viewerTableNumber]
  );

  const rotateKotSegment = useCallback(() => {
    const segs = kotSegmentsRef.current;
    const live = liveCaptureRef.current;
    if (!segs || !live || segs.pending) return segs?.pending || null;
    const clipKey = clipKeyRef.current;
    const meta = clipMetaRef.current;
    const kotMatchId = kotMatchIdFromClipKey(clipKey) || meta?.kotMatchId;
    if (!kotMatchId || !meta?.teId) return null;
    const run = (async () => {
      // Same order as championship pieces: close the mic slice, then the picture.
      const audioBlob = await sidecarAudioRef.current?.rotate().catch(() => null);
      if (!sidecarAudioRef.current) {
        // Mic granted mid-match: record it from the next piece on.
        const mic = streamRef.current
          ?.getAudioTracks?.()
          .find((track) => track.readyState === "live");
        if (mic) {
          mic.enabled = true;
          sidecarAudioRef.current = createSidecarAudioRecorder(mic);
        }
      }
      let blob = null;
      try {
        blob = await live.finishSegment();
      } catch (err) {
        appendLiveTrainingEvalDebug("kot_segment_failed", {
          clipKey,
          message: err?.message || String(err),
        });
        return;
      }
      if (!blob || blob.size < 1024) return;
      const segmentIndex = segs.nextIndex;
      segs.nextIndex += 1;
      appendLiveTrainingEvalDebug("kot_segment_queued", {
        clipKey,
        segmentIndex,
        bytes: blob.size,
      });
      enqueueKotSegmentUpload({
        clipKey,
        meta,
        kotMatchId,
        segmentIndex,
        blob,
        audioBlob,
      });
    })().finally(() => {
      segs.pending = null;
    });
    segs.pending = run;
    return run;
  }, []);

  const startRecording = useCallback(
    async (clipKey) => {
      if (recordingRef.current || startLockRef.current) return;
      const cam = streamRef.current;
      if (!cam) return;
      startLockRef.current = true;
      const gen = captureGenRef.current;
      try {
        // The sidecar clones the mic as it is now: a missing or parked track
        // means a silent clip for the whole recording.
        await ensureEvalStreamHasAudio(cam, { timeoutMs: 1500 });
        syncMicState(cam);
        const video = videoRef?.current;
        const live = video
          ? await createLiveH264Session({
              width: EVAL_VIDEO_CAPTURE.width,
              height: EVAL_VIDEO_CAPTURE.height,
              fps: fpsRef.current || DEFAULT_WEB_EVAL_FPS,
              // Uploaded eval clips are the Low tier (~8 MB/min), same as the
              // old playback remux. The live encoder writes that file directly.
              bitrate: evalVideoBitRate("low"),
              paint(ctx, w, h) {
                if (!video || video.readyState < 2) return false;
                const { flipH: fh, flipV: fv } = flipRef.current;
                ctx.save();
                ctx.setTransform(
                  fh ? -1 : 1,
                  0,
                  0,
                  fv ? -1 : 1,
                  fh ? w : 0,
                  fv ? h : 0
                );
                const digitalZoom =
                  !hardwareZoomRef.current && zoomRef.current > 1.001
                    ? zoomRef.current
                    : 1;
                if (digitalZoom > 1.001) {
                  const crop = digitalCropSourceRect(
                    video.videoWidth || w,
                    video.videoHeight || h,
                    digitalZoom
                  );
                  ctx.drawImage(
                    video,
                    crop.sx,
                    crop.sy,
                    crop.sw,
                    crop.sh,
                    0,
                    0,
                    w,
                    h
                  );
                } else {
                  ctx.drawImage(video, 0, 0, w, h);
                }
                ctx.restore();
                return true;
              },
            })
          : null;
        if (gen !== captureGenRef.current) {
          live?.close();
          return;
        }
        if (live) {
          replayDebug("eval_start", { mode: "webcodecs" });
          liveCaptureRef.current = live;
          const mic =
            cam.getAudioTracks?.().find((track) => track.readyState === "live") ||
            null;
          sidecarAudioRef.current = createSidecarAudioRecorder(mic);
          try {
            video?.play?.();
          } catch {
            /* keep decoder alive while recording */
          }
          setLiveEncoding(true);
          armClipMeta(clipKey);
          if (kotMatchIdFromClipKey(clipKey)) {
            const segs = { timer: 0, nextIndex: 0, pending: null };
            segs.timer = window.setInterval(() => {
              if (kotSegmentsRef.current === segs) void rotateKotSegment();
            }, MATCH_RECORD_CHUNK_MS);
            kotSegmentsRef.current = segs;
          }
          return;
        }
        setLiveEncoding(false);

        const mimeType = pickEvalVideoMimeType();
        const mixed = buildRecordStream();
        if (!mixed) return;
        recordStreamRef.current = mixed === cam ? null : mixed;
        let recorder;
        try {
          recorder = createEvalMediaRecorder(mixed, videoQuality, mimeType);
        } catch (err) {
          console.warn(
            "Live training eval MediaRecorder failed:",
            err?.message || err
          );
          appendLiveTrainingEvalDebug(
            "mediarecorder_failed",
            err?.message || String(err)
          );
          return;
        }
        chunksRef.current = [];
        recorder.ondataavailable = (ev) => {
          if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
        };
        try {
          recorder.start(EVAL_RECORD_TIMESLICE_MS);
        } catch {
          try {
            recorder.start();
          } catch (err) {
            console.warn("Live training eval recorder start failed:", err);
            return;
          }
        }
        try {
          videoRef?.current?.play?.();
        } catch {
          /* keep decoder alive while recording */
        }
        recorderRef.current = recorder;
        replayDebug("eval_start", {
          mode: "mediarecorder",
          mime: recorder.mimeType || mimeType || "",
        });
        armClipMeta(clipKey);
      } finally {
        startLockRef.current = false;
      }
    },
    [
      armClipMeta,
      buildRecordStream,
      rotateKotSegment,
      syncMicState,
      videoQuality,
      videoRef,
    ]
  );

  const stopAndUpload = useCallback(async () => {
    captureGenRef.current += 1;
    const live = liveCaptureRef.current;
    const recorder = recorderRef.current;
    const clipKey = clipKeyRef.current;
    const meta = clipMetaRef.current;
    if (!live && (!recorder || !recordingRef.current)) return;
    const kotSegs = kotSegmentsRef.current;
    kotSegmentsRef.current = null;
    if (kotSegs?.timer) window.clearInterval(kotSegs.timer);
    recordingRef.current = false;
    setRecording(false);
    setLiveEncoding(false);
    liveCaptureRef.current = null;
    const sidecar = live ? sidecarAudioRef.current : null;
    if (live) {
      sidecarAudioRef.current = null;
      recorderRef.current = null;
    }
    if (kotSegs?.pending) {
      try {
        await kotSegs.pending;
      } catch {
        /* a lost piece is logged by its own job */
      }
    }

    let blob = null;
    let sidecarAudio = null;
    if (live) {
      try {
        sidecarAudio = (await sidecar?.stop()) || null;
      } catch (err) {
        console.warn("Eval audio stop failed:", err?.message || err);
      }
      try {
        blob = await live.stop();
      } catch (err) {
        console.warn("Live H.264 capture stop failed:", err?.message || err);
      }
    } else blob = await new Promise((resolve) => {
      const finish = () => {
        const type =
          recorder.mimeType ||
          chunksRef.current[0]?.type ||
          "video/mp4";
        const parts = chunksRef.current;
        chunksRef.current = [];
        if (!parts.length) {
          resolve(null);
          return;
        }
        resolve(new Blob(parts, { type }));
      };
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = finish;
      try {
        // Chrome requestData() before stop() writes a cluster the player cannot
        // advance past. Timeslice already flushed; stop() emits the last chunk.
        // iOS: do not requestData mid-stream — those MP4 fragments concat to ~1s
        // for ffmpeg even when Safari plays more.
        if (recorder.state !== "inactive") recorder.stop();
        else finish();
      } catch {
        finish();
      }
    });

    recorderRef.current = null;
    clipKeyRef.current = null;
    clipMetaRef.current = null;
    if (recordStreamRef.current && recordStreamRef.current !== streamRef.current) {
      recordStreamRef.current.getVideoTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }

    const teId = meta?.teId;
    const kotMatchId = kotMatchIdFromClipKey(clipKey) || meta?.kotMatchId;
    // Pieces already on the server still need their finalize.
    const kotStaged = Boolean(live && kotSegs && kotMatchId);
    const hasPieces = kotStaged && kotSegs.nextIndex > 0;
    if ((!hasPieces && (!blob || blob.size < 1024)) || !teId || !clipKey) {
      console.warn("Live training eval clip skipped:", {
        size: blob?.size || 0,
        teId,
        clipKey,
      });
      appendLiveTrainingEvalDebug("clip_skipped", {
        size: blob?.size || 0,
        teId,
        clipKey,
      });
      setStatus("error");
      return;
    }

    if (!beginEvalClipUpload(clipKey)) {
      appendLiveTrainingEvalDebug("upload_claim_blocked", {
        clipKey,
        claimed: evalClipKeyClaimed(clipKey),
      });
      setStatus(evalClipKeyClaimed(clipKey) ? "uploaded" : "idle");
      return;
    }

    const seatedMeta = tablePhoneRef.current
      ? buildEvalClipUploadMeta({
          teId: meta?.teId,
          sequenceNumber: meta?.sequenceNumber,
          viewerPlayerId: meta?.playerId,
          tableNumber: meta?.tableNumber,
          cameraPosition: meta?.cameraPosition,
          cameraSetup: meta?.cameraSetup,
          recordedAt: meta?.viewSetup?.recorded_at,
          flipH: meta?.viewSetup?.flip_h,
          flipV: meta?.viewSetup?.flip_v,
          kotMatchId,
          tablePhone: true,
          tablePlayers: tablePlayersRef.current,
          tableNumbers: tablePhoneTablesRef.current,
        })
      : null;
    const uploadMeta = seatedMeta || meta;
    const uploadPlayerId = resolveUploadPlayerId(uploadMeta);
    uploadInflightRef.current += 1;
    setUploading(true);
    setStatus("uploading");
    appendLiveTrainingEvalDebug("encode_upload_queued", {
      clipKey,
      blobSize: blob?.size || 0,
      blobType: blob?.type || "",
      playerId: uploadPlayerId,
    });
    enqueueEvalClipJob(async () => {
      try {
        if (kotStaged) {
          const args = {
            ...kotUploadArgs(meta, kotMatchId),
            playerId: uploadPlayerId,
            tableNumber: uploadMeta?.tableNumber ?? meta?.tableNumber,
            cameraPosition: uploadMeta?.cameraPosition ?? meta?.cameraPosition,
          };
          if (blob?.size >= 1024) {
            try {
              // Already inside the serial queue: upload directly, never re-enqueue.
              await runEvalUpload("kot_segment", {
                args: { ...args, segmentIndex: kotSegs.nextIndex },
                blob,
                audioBlob: sidecarAudio,
              });
            } catch (segErr) {
              appendLiveTrainingEvalDebug("kot_segment_upload_failed", {
                clipKey,
                segmentIndex: kotSegs.nextIndex,
                message: segErr?.message || String(segErr),
              });
            }
          }
          stopCaptureQosSampling();
          appendCaptureQos("recording_stop", { clip_id: clipKey });
          const identified = identityPlayersRef.current;
          const result = await runEvalUpload("kot_finalize", {
            args: {
              ...args,
              viewSetup: stampIdentityOnViewSetup(
                { ...(uploadMeta?.viewSetup || {}), ...args.viewSetup },
                identified?.confirmed ? identified : null
              ),
              recordedAt: uploadMeta?.viewSetup?.recorded_at || new Date(),
              clientUploadId: meta?.clientUploadId || null,
              finalizeCapture: true,
            },
          });
          appendLiveTrainingEvalDebug(
            result?.saved_on_phone ? "kot_saved_on_phone" : "upload_ok",
            { clipKey, pieces: kotSegs.nextIndex + (blob?.size >= 1024 ? 1 : 0) }
          );
          setStatus(result?.saved_on_phone ? "saved_on_phone" : "uploaded");
          return;
        }
        const remux = isLiveH264Blob(blob)
          ? {
              blob,
              encoded: true,
              encodeWallMs: liveH264EncodeWallMs(blob),
              mimeType: "video/mp4",
            }
          : await transcodeEvalClipToLowH264(blob);
        const encoded = remux?.blob || blob;
        const mimeType = remux?.mimeType || encoded.type || blob.type;
        const encodeWallMs = remux?.encodeWallMs ?? null;
        const durationGuessSec = Math.max(
          1,
          Number(encodeWallMs) > 0 ? Number(encodeWallMs) / 1000 : 60
        );
        appendLiveTrainingEvalDebug("encode_done", {
          clipKey,
          inSize: blob.size,
          outSize: encoded.size,
          outType: mimeType,
          clientEncoded: Boolean(remux?.encoded),
          encodeWallMs,
        });
        appendCaptureQos("encode_done", {
          clip_id: clipKey,
          bytes: encoded.size,
          encode_wall_ms: encodeWallMs,
          bitrate_target: evalVideoBitRate("low"),
          bitrate_effective: effectiveBitrateBps(encoded.size, durationGuessSec),
          path: isLiveH264Blob(blob) ? "webcodecs" : "mediarecorder",
          codec: "h264",
          container: evalVideoExtension(mimeType),
          ok: true,
        });
        stopCaptureQosSampling();
        appendCaptureQos("recording_stop", { clip_id: clipKey });
        const ext = evalVideoExtension(mimeType);
        const baseSetup =
          uploadMeta?.viewSetup && typeof uploadMeta.viewSetup === "object"
            ? { ...uploadMeta.viewSetup }
            : {};
        const identified = identityPlayersRef.current;
        const viewSetup = stampIdentityOnViewSetup(
          {
            ...baseSetup,
            client_codec: "h264",
            client_encoded: remux?.encoded === true,
            client_container: "mp4",
            encode_wall_ms:
              remux?.encodeWallMs != null && Number(remux.encodeWallMs) > 0
                ? Math.round(Number(remux.encodeWallMs))
                : baseSetup.encode_wall_ms || null,
          },
          identified?.confirmed ? identified : null
        );
        const result = await runEvalUpload("eval_clip", {
          args: {
            mimeType,
            teId,
            playerId: uploadPlayerId,
            sequenceNumber: kotMatchId ? null : meta?.sequenceNumber,
            kotMatchId,
            recordedAt: uploadMeta?.viewSetup?.recorded_at || new Date(),
            tableNumber: uploadMeta?.tableNumber ?? meta?.tableNumber,
            cameraPosition: uploadMeta?.cameraPosition ?? meta?.cameraPosition,
            viewSetup,
            clientUploadId: meta?.clientUploadId || null,
            filename: evalClipFilename({
              tableNumber: meta?.tableNumber,
              cameraPosition: meta?.cameraPosition,
              cameraSetup: meta?.cameraSetup,
              sequenceNumber: kotMatchId ? null : meta?.sequenceNumber,
              teId,
              ext,
              kotMatchId,
            }),
          },
          blob: encoded,
          // The server mux runs after the clip row exists, off the serial queue.
          audioBlob: isLiveH264Blob(encoded) ? sidecarAudio : null,
        });
        if (result?.already_exists) {
          console.info("Live training eval clip already on server:", clipKey);
        }
        appendLiveTrainingEvalDebug(
          result?.saved_on_phone ? "saved_on_phone" : "upload_ok",
          { clipKey, alreadyExists: Boolean(result?.already_exists) }
        );
        setStatus(result?.saved_on_phone ? "saved_on_phone" : "uploaded");
      } catch (err) {
        console.warn("Live training eval upload failed:", err?.message || err);
        appendLiveTrainingEvalDebug("upload_failed", {
          clipKey,
          message: err?.message || String(err),
        });
        releaseEvalClipClaim(clipKey);
        setStatus("error");
      } finally {
        finishEvalClipUpload(clipKey);
        uploadInflightRef.current = Math.max(0, uploadInflightRef.current - 1);
        setUploading(uploadInflightRef.current > 0);
      }
    });
  }, []);

  stopAndUploadRef.current = stopAndUpload;

  const openCalibration = useCallback(async () => {
    setCalibrationOpen(true);
    await startCamera();
    // Prefer a digital preview while calibrating so pinch stays fluid; optical
    // zoom is re-applied when the overlay closes.
    const stream = streamRef.current;
    if (zoomRef.current < 1 && stream) {
      await applyZoomToStream(stream, zoomRef.current);
    } else {
      await resetTrackToIdentityZoom(stream);
    }
    const video = videoRef?.current;
    try {
      await video?.play?.();
    } catch {
      /* ignore */
    }
  }, [applyZoomToStream, resetTrackToIdentityZoom, startCamera, videoRef]);

  const closeCalibration = useCallback(() => {
    setCalibrationOpen(false);
    if (streamRef.current) {
      applyZoomToStream(streamRef.current, zoomRef.current);
    }
  }, [applyZoomToStream]);

  useEffect(() => {
    if (!captureAllowed) {
      stopAndUploadRef.current?.();
      if (!calibrationOpen) releaseCameraSession();
      return undefined;
    }
    return undefined;
  }, [captureAllowed, calibrationOpen, releaseCameraSession]);

  useEffect(() => {
    return () => {
      recordingRef.current = false;
      if (kotSegmentsRef.current?.timer) {
        window.clearInterval(kotSegmentsRef.current.timer);
      }
      kotSegmentsRef.current = null;
      try {
        liveCaptureRef.current?.close();
        liveCaptureRef.current = null;
        sidecarAudioRef.current?.stop?.();
        sidecarAudioRef.current = null;
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      releaseCameraSession();
    };
  }, [releaseCameraSession]);

  useEffect(() => startEvalUploadBacklogFlusher(enqueueEvalClipJob), []);

  useEffect(() => {
    if (!captureAllowed && !calibrationOpen) return undefined;
    refreshCameras();
    const media = navigator?.mediaDevices;
    if (!media || typeof media.addEventListener !== "function") {
      return undefined;
    }
    const onDeviceChange = () => {
      refreshCameras();
    };
    media.addEventListener("devicechange", onDeviceChange);
    return () => media.removeEventListener("devicechange", onDeviceChange);
  }, [captureAllowed, calibrationOpen, refreshCameras]);

  const teId = exercise?.te_id;
  const evalClipKey = teId ? `${teId}:${seqNumber || 1}` : null;
  const clipKey = isKot ? kotTarget?.clipKey || null : evalClipKey;
  const inEvalWindow =
    captureAllowed &&
    isStarted &&
    !isWaiting &&
    !isPaused &&
    remainingSec != null &&
    Number.isFinite(Number(remainingSec)) &&
    Number(remainingSec) > 0 &&
    Number(remainingSec) <= windowSec &&
    !countdownBlocksRecording;
  const inRecordWindow = isKot
    ? Boolean(captureAllowed && kotTarget?.clipKey)
    : inEvalWindow;

  const cameraPrewarmSec =
    isTeSequences(exercise) &&
    sequenceDurationSec > 0 &&
    sequenceDurationSec <= windowSec
      ? sequenceDurationSec
      : EVAL_CAMERA_PREWARM_SEC;
  const inCameraPrewarmWindow =
    captureAllowed &&
    !isKot &&
    isStarted &&
    !isWaiting &&
    !isPaused &&
    remainingSec != null &&
    Number.isFinite(Number(remainingSec)) &&
    Number(remainingSec) > 0 &&
    Number(remainingSec) <= windowSec + cameraPrewarmSec;

  const replayWarm =
    Boolean(enabled) &&
    isInstantReplayEnabled(tsId) &&
    isClientCameraRecordingEnabled() &&
    captureSettings.instantReplayEnabled !== false &&
    isStarted &&
    !isWaiting &&
    !isPaused;
  const wantCamera =
    calibrationOpen ||
    (isKot && kotPrewarm) ||
    (captureAllowed && !isKot && inCameraPrewarmWindow) ||
    replayWarm ||
    recording ||
    coachRemoteActiveRef.current ||
    coachCalibHold;

  startCameraRef.current = startCamera;
  startRecordingRef.current = startRecording;

  useEffect(() => {
    identityStopRef.current = { exercise: false, eval: false };
    identityPlayersRef.current = null;
  }, [exercise?.te_id, viewerTableNumber]);

  useEffect(() => {
    const tableNumber = Number(viewerTableNumber);
    const teId = exercise?.te_id;
    const onTableCamera = Boolean(
      enabled &&
        cameraReady &&
        tsId &&
        teId &&
        tableNumber > 0 &&
        (!coachDevice || tablePhone)
    );
    const liveExercise = Boolean(
      onTableCamera && isStarted && !isPaused && !isWaiting
    );
    const liveEval = Boolean(onTableCamera && recording);
    if (!liveExercise && !liveEval) return undefined;

    const checkpoint = liveEval ? "eval" : "exercise";
    const gen = identityGenRef.current + 1;
    identityGenRef.current = gen;
    identityInflightRef.current = false;
    let stopped = false;

    const tick = async () => {
      if (stopped || identityGenRef.current !== gen) return;
      if (identityInflightRef.current) return;
      if (identityStopRef.current[checkpoint]) return;
      const video = videoRef?.current;
      const jpeg = captureVideoFrameJpeg(video, 0.72);
      if (!jpeg) return;
      identityInflightRef.current = true;
      try {
        const data = await identifyTablePlayers({
          frameJpeg: jpeg,
          teId: String(teId),
          tsId: String(tsIdRef.current || tsId),
          tableNumber,
          checkpoint,
          viewSetup: {
            flip_h: flipRef.current.flipH === true,
            flip_v: flipRef.current.flipV === true,
          },
          videoSize: {
            w: video?.videoWidth || 0,
            h: video?.videoHeight || 0,
          },
        });
        if (stopped || identityGenRef.current !== gen) return;
        if (data?.confirmed) {
          identityStopRef.current[checkpoint] = true;
          window.clearInterval(timer);
          if (checkpoint === "eval") {
            identityPlayersRef.current = data;
            if (clipMetaRef.current?.viewSetup) {
              clipMetaRef.current.viewSetup = stampIdentityOnViewSetup(
                clipMetaRef.current.viewSetup,
                data
              );
            }
          }
        }
      } catch {
        /* next frame in 2 seconds */
      } finally {
        if (identityGenRef.current === gen) identityInflightRef.current = false;
      }
    };

    const timer = window.setInterval(tick, IDENTITY_SAMPLE_MS);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [
    enabled,
    cameraReady,
    tsId,
    exercise?.te_id,
    viewerTableNumber,
    coachDevice,
    tablePhone,
    isStarted,
    isPaused,
    isWaiting,
    recording,
    videoRef,
  ]);

  useEffect(() => {
    if (!enabled && !calibrationOpen) return undefined;
    if (!wantCamera && !coachRemoteActiveRef.current) {
      if (!calibrationOpen) stopCamera();
      return undefined;
    }
    startCamera();
    return undefined;
  }, [enabled, calibrationOpen, wantCamera, startCamera, stopCamera]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!inRecordWindow) {
        resetEvalPreCaptureCountdownDedupe();
        // Coach-triggered table capture must survive outside the autoeval window.
        if (recordingRef.current && !coachRemoteActiveRef.current) {
          await stopAndUploadRef.current?.();
        }
        return;
      }
      if (coachRemoteActiveRef.current) return;
      if (!cameraReady || !clipKey) return;
      if (evalClipKeyClaimed(clipKey)) return;
      if (recordingRef.current && clipKeyRef.current === clipKey) return;
      if (recordingRef.current && clipKeyRef.current !== clipKey) {
        await stopAndUploadRef.current?.();
        if (cancelled) return;
      }
      if (evalClipKeyClaimed(clipKey) || recordingRef.current) return;
      startRecording(clipKey);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [inRecordWindow, cameraReady, clipKey, startRecording, countdownBlocksRecording]);

  // Coach multi-table / table-cam: start/stop the same eval MediaRecorder so
  // clips save as autoeval videos and the bottom-left debug panel logs them.
  useEffect(() => {
    // Managing coach UI never captures; table-phone on a dual-role account may.
    if (!socket || !enabled || (coachDevice && !tablePhone)) return undefined;

    const matchesTable = (payload) => {
      const mine = Number(viewerTableNumberRef.current);
      const theirs = Number(payload?.table_number);
      return (
        Number.isFinite(mine) &&
        mine > 0 &&
        Number.isFinite(theirs) &&
        theirs === mine
      );
    };

    const isCoachTableSource = (payload) => {
      const src = String(payload?.source || "")
        .trim()
        .toLowerCase();
      return (
        src === COACH_TABLE_VIDEO_SOURCE ||
        (src === "coach_video" && payload?.table_number != null)
      );
    };

    const onStart = (payload = {}) => {
      if (!isCoachTableSource(payload)) return;
      // Table phones record via useOffTableChronoRecorder (direct MediaRecorder).
      // That path stays alive even when eval-capture UI is off, and is more
      // reliable on Android Chrome than the canvas capture pipeline.
      if (tablePhoneRef.current) {
        appendLiveTrainingEvalDebug("coach_table_start_ignored", {
          reason: "table_phone_uses_chrono_recorder",
          table: viewerTableNumberRef.current,
        });
        return;
      }
      if (!matchesTable(payload)) {
        appendLiveTrainingEvalDebug("coach_table_start_ignored", {
          reason: "table_mismatch",
          mine: viewerTableNumberRef.current,
          theirs: payload?.table_number,
        });
        return;
      }
      const teId =
        payload?.te_id != null
          ? String(payload.te_id)
          : exerciseRef.current?.te_id != null
            ? String(exerciseRef.current.te_id)
            : null;
      if (!teId) {
        appendLiveTrainingEvalDebug("coach_table_start_ignored", {
          reason: "missing_te_id",
        });
        return;
      }

      const captureId =
        payload?.capture_id != null ? String(payload.capture_id) : "";
      const clipKey = `coach_table:${teId}:${captureId || Date.now()}`;

      appendLiveTrainingEvalDebug("coach_table_start", {
        table: payload?.table_number,
        teId,
        captureId: captureId || null,
        fps: payload?.fps ?? null,
      });

      coachRemoteActiveRef.current = true;
      void (async () => {
        try {
          const stream = await startCameraRef.current?.();
          if (!stream) {
            appendLiveTrainingEvalDebug("coach_table_start_failed", {
              reason: "camera_unavailable",
            });
            coachRemoteActiveRef.current = false;
            return;
          }
          if (recordingRef.current) {
            // Already capturing (autoeval window) — keep rolling until coach stop.
            appendLiveTrainingEvalDebug("coach_table_join_active", {
              clipKey: clipKeyRef.current,
            });
            if (clipMetaRef.current?.viewSetup) {
              clipMetaRef.current.viewSetup.source = COACH_TABLE_VIDEO_SOURCE;
              clipMetaRef.current.viewSetup.coach_triggered = true;
              clipMetaRef.current.viewSetup.capture_id = captureId || null;
            }
            return;
          }
          startRecordingRef.current?.(clipKey);
          if (clipMetaRef.current?.viewSetup) {
            clipMetaRef.current.viewSetup.source = COACH_TABLE_VIDEO_SOURCE;
            clipMetaRef.current.viewSetup.coach_triggered = true;
            clipMetaRef.current.viewSetup.autoeval = true;
            clipMetaRef.current.viewSetup.capture_id = captureId || null;
          }
          if (!recordingRef.current) {
            appendLiveTrainingEvalDebug("coach_table_start_failed", {
              reason: "recorder_not_started",
            });
            coachRemoteActiveRef.current = false;
          }
        } catch (err) {
          appendLiveTrainingEvalDebug("coach_table_start_failed", {
            reason: err?.message || String(err),
          });
          coachRemoteActiveRef.current = false;
        }
      })();
    };

    const onStop = (payload = {}) => {
      if (!isCoachTableSource(payload) && !coachRemoteActiveRef.current) return;
      if (payload?.table_number != null && !matchesTable(payload)) return;
      if (!coachRemoteActiveRef.current && !recordingRef.current) return;

      appendLiveTrainingEvalDebug("coach_table_stop", {
        table: payload?.table_number,
        captureId: payload?.capture_id || null,
        wasRecording: recordingRef.current,
      });

      coachRemoteActiveRef.current = false;
      if (recordingRef.current) {
        void stopAndUploadRef.current?.();
      }
    };

    socket.on("tableVideoRecordingStart", onStart);
    socket.on("tableVideoRecordingStop", onStop);
    appendLiveTrainingEvalDebug("coach_table_listener_ready", {
      table: viewerTableNumberRef.current,
      tablePhone: Boolean(tablePhone),
    });
    return () => {
      socket.off("tableVideoRecordingStart", onStart);
      socket.off("tableVideoRecordingStop", onStop);
    };
  }, [socket, enabled, coachDevice, tablePhone]);

  // Coach calibrates this phone's camera: one JPEG after each settings change.
  useEffect(() => {
    if (!socket || !enabled || (coachDevice && !tablePhone)) return undefined;

    const enqueue = (job) => {
      const run = calibChainRef.current.then(job, job);
      calibChainRef.current = run.then(
        () => {},
        () => {}
      );
    };

    const onRequest = (payload = {}) => {
      const tsId = payload?.ts_id != null ? String(payload.ts_id) : "";
      if (!tsId || tsId !== String(tsIdRef.current || "")) return;
      const requestId =
        payload?.request_id != null ? String(payload.request_id) : "";
      const replySocketId =
        payload?.reply_socket_id != null
          ? String(payload.reply_socket_id)
          : "";
      const token = ++coachCalibSeqRef.current;
      armCoachCalibHold();
      enqueue(async () => {
        if (token !== coachCalibSeqRef.current) return;
        let result;
        try {
          result = await applyCoachCameraCalibrationRef.current?.({
            reason: payload?.reason,
            settings: payload?.settings,
          });
        } catch (err) {
          result = { ok: false, code: "camera_unavailable" };
          appendLiveTrainingEvalDebug(
            "coach_calib_failed",
            err?.message || String(err)
          );
        }
        if (token !== coachCalibSeqRef.current) return;
        socket.emit("playerCameraCalibrationFrame", {
          ts_id: tsId,
          table_number: payload?.table_number,
          request_id: requestId,
          reply_socket_id: replySocketId,
          ...(result || { ok: false, code: "camera_unavailable" }),
        });
      });
    };

    const onRelease = (payload = {}) => {
      const tsId = payload?.ts_id != null ? String(payload.ts_id) : "";
      if (tsId && tsId !== String(tsIdRef.current || "")) return;
      coachCalibSeqRef.current += 1;
      releaseCoachCalibHold();
    };

    socket.on("playerCameraCalibrationRequest", onRequest);
    socket.on("playerCameraCalibrationRelease", onRelease);
    return () => {
      socket.off("playerCameraCalibrationRequest", onRequest);
      socket.off("playerCameraCalibrationRelease", onRelease);
      window.clearTimeout(calibHoldTimerRef.current);
    };
  }, [
    socket,
    enabled,
    coachDevice,
    tablePhone,
    armCoachCalibHold,
    releaseCoachCalibHold,
  ]);

  const debugSnapshot = useMemo(
    () => ({
      status,
      cameraReady,
      cameraError: cameraError || null,
      recording,
      uploading,
      captureAllowed,
      inRecordWindow,
      inCameraPrewarmWindow,
      clipKey: clipKey || null,
      remainingSec,
      windowSec,
      isWaiting,
      isStarted,
      teId: exercise?.te_id || null,
      seqNumber,
      countdownBlocksRecording,
      clipClaimed: clipKey ? evalClipKeyClaimed(clipKey) : false,
      viewerPlayerId: viewerPlayerId || null,
      viewerTableNumber: viewerTableNumber ?? null,
      tablePhone: Boolean(tablePhone),
      coachRemote: coachRemoteActiveRef.current,
    }),
    [
      status,
      cameraReady,
      cameraError,
      recording,
      uploading,
      captureAllowed,
      inRecordWindow,
      inCameraPrewarmWindow,
      clipKey,
      remainingSec,
      windowSec,
      isWaiting,
      isStarted,
      exercise?.te_id,
      seqNumber,
      countdownBlocksRecording,
      viewerPlayerId,
      viewerTableNumber,
      tablePhone,
    ]
  );

  const prevDebugRef = useRef("");
  const lastStateLogAtRef = useRef(0);
  useEffect(() => {
    const key = JSON.stringify(debugSnapshot);
    if (key === prevDebugRef.current) return;
    prevDebugRef.current = key;
    const important =
      debugSnapshot.recording ||
      debugSnapshot.uploading ||
      debugSnapshot.status === "error" ||
      debugSnapshot.status === "uploaded" ||
      debugSnapshot.status === "saved_on_phone";
    const now = Date.now();
    if (!important && now - lastStateLogAtRef.current < 4000) return;
    lastStateLogAtRef.current = now;
    appendLiveTrainingEvalDebug("state", debugSnapshot);
  }, [debugSnapshot]);

  const getMediaStream = useCallback(() => streamRef.current, []);

  /** Tap handler: asks for the mic again (Android shows the prompt on a gesture). */
  const enableMicrophone = useCallback(async () => {
    const stream = streamRef.current;
    if (!stream) return false;
    await ensureEvalStreamHasAudio(stream);
    const info = syncMicState(stream);
    appendLiveTrainingEvalDebug("mic_retry", info);
    return info.has_audio;
  }, [syncMicState]);

  /** Live calibration transform for the table-replay ring (zoom / flip). */
  const getReplayCaptureTransform = useCallback(
    () => ({
      zoom: zoomRef.current,
      hardwareZoom: Boolean(hardwareZoomRef.current),
      flipH: Boolean(flipRef.current?.flipH),
      flipV: Boolean(flipRef.current?.flipV),
    }),
    []
  );

  /** True for the whole eval/KoT capture, in both MediaRecorder and WebCodecs mode. */
  const isEvalRecording = useCallback(
    () =>
      Boolean(
        recordingRef.current ||
          liveCaptureRef.current ||
          recorderRef.current?.state === "recording"
      ),
    []
  );

  return {
    recordingEnabled: captureAllowed,
    replayWarm,
    getMediaStream,
    getReplayCaptureTransform,
    isEvalRecording,
    calibrationOpen,
    openCalibration,
    closeCalibration,
    flipH,
    flipV,
    toggleFlipH,
    toggleFlipV,
    fps,
    cameraView,
    setCameraView,
    cameras,
    facingMode,
    deviceId,
    setCaptureFps,
    setCaptureCamera,
    setCaptureVideoQuality,
    setCaptureDurationSec,
    videoQuality,
    windowSec,
    recordingResolution,
    captureLocked,
    zoom,
    zoomRange,
    hardwareZoom,
    setCaptureZoom,
    setCaptureWideZoom,
    cameraReady,
    cameraError,
    micError,
    enableMicrophone,
    cameraSwitching,
    recording,
    uploading,
    status,
    captureMode: isKot ? "kot" : "eval",
    stream: streamRef.current,
    debugSnapshot,
  };
}
